/**
 * ForgotPassword Component - Customer App
 *
 * Password reset request page for CFleet Customer Portal.
 * Integrates with @cfleet/api-client useCustomerRequestPasswordReset hook.
 *
 * Flow:
 * 1. Customer enters their email address
 * 2. Form submits to POST /customer/auth/password/reset
 * 3. On success, show "check your email" message
 * 4. Customer clicks link in email and goes to /reset-password with token
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCustomerRequestPasswordReset } from '@cfleet/api-client';
import {
  Command,
  ArrowRight,
  ArrowLeft,
  Mail,
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { mutate: requestReset, isPending } = useCustomerRequestPasswordReset();

  // Form state
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    requestReset(
      { email },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : 'An error occurred');
        },
      }
    );
  };

  // Clear error when user starts typing
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (error) setError(null);
    setEmail(e.target.value);
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4 font-sans text-zinc-900 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-96 bg-gradient-to-b from-blue-50 to-transparent -z-10"></div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
        {!success ? (
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                  <Command size={20} />
                </div>
                <span className="font-bold text-3xl text-zinc-800 tracking-tight">cfleet</span>
              </div>
            </div>

            <div className="mb-8 text-center">
              <h2 className="text-xl font-bold text-zinc-900">Forgot your password?</h2>
              <p className="text-sm text-zinc-500 mt-2">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-700">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-medium text-zinc-700 mb-1.5"
                >
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
                    <Mail size={18} />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={handleEmailChange}
                    disabled={isPending}
                    className="w-full pl-10 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-zinc-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isPending}
                className="w-full bg-zinc-900 text-white py-3 rounded-lg text-sm font-semibold shadow-lg hover:bg-zinc-800 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-lg"
              >
                {isPending ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    Send Reset Link <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-2">Check your email</h2>
            <p className="text-sm text-zinc-500 mb-2">
              We've sent a password reset link to:
            </p>
            <p className="text-sm font-semibold text-zinc-800 mb-6">{email}</p>
            <p className="text-xs text-zinc-400 mb-6">
              If you don't see the email, check your spam folder.
            </p>
          </div>
        )}

        <div className="px-8 py-5 bg-zinc-50 border-t border-zinc-100 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-600 font-medium hover:text-blue-600 transition-colors"
          >
            <ArrowLeft size={14} /> Back to Sign In
          </Link>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full w-fit mx-auto border border-emerald-100">
          <ShieldCheck size={12} /> Secure Connection
        </div>
        <p className="text-xs text-zinc-400">© 2025 cfleet. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ForgotPassword;
